import { useState } from "react";
import { Table, Tag, Space, Tooltip, Typography, Button, Popconfirm } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

const { Text } = Typography;

const clamp = (lineas) => ({ display: "-webkit-box", WebkitLineClamp: lineas, WebkitBoxOrient: "vertical", overflow: "hidden" });

export default function OrdenesServicioPOTable({ data, loading, onEdit, onDelete, onDeleteSelected, permissionDelete, permissionUpdate }) {
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10
  });

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => {
      setSelectedRowKeys(keys);
    }
  };

  const handleDeleteSelected = async () => {
    await onDeleteSelected(selectedRowKeys);
    setSelectedRowKeys([]);
  };
  const formatNumber = (num) =>
    new Intl.NumberFormat("es-CO").format(Number(num || 0));

  const formatMoney = (num) =>
    new Intl.NumberFormat("es-CO", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Number(num || 0));

  const columns = [
    {
      title: "PO",
      dataIndex: "Numero_PO",
      width: 120,
      render: (value) => <Text strong>{value}</Text>
    },
    {
      title: "Producto",
      width: 300,
      render: (_, record) => {
        const detalle = record.descripcion || record.tipo_producto;
        return (
          <Tooltip
            title={
              <>
                <div>{record.nombre_producto}</div>
                {detalle && <div style={{ marginTop: 4, color: "#bbb" }}>{detalle}</div>}
              </>
            }
          >
            <div style={{ maxWidth: 300 }}>
              <Text strong style={clamp(2)}>{record.nombre_corto || record.nombre_producto}</Text>
              {detalle && <Text type="secondary" style={{ fontSize: 12, ...clamp(1) }}>{detalle}</Text>}
            </div>
          </Tooltip>
        );
      }
    },
    {
      title: "Proyecto / Municipio",
      width: 240,
      render: (_, record) => (
        <Space direction="vertical" size={0} style={{ maxWidth: 240 }}>
          {record.codigo
            ? (
              <Tooltip title={record.proyecto}>
                <Space size={4}>
                  <Tag color="blue" style={{ marginRight: 0 }}>{record.codigo}</Tag>
                  <Text style={{ maxWidth: 170 }} ellipsis>{record.proyecto}</Text>
                </Space>
              </Tooltip>
            )
            : <Text type="secondary">Sin proyecto</Text>}
          {record.municipio && <Text type="secondary" style={{ fontSize: 12 }}>{record.municipio}, {record.departamento}</Text>}
        </Space>
      )
    },
    {
      title: "Proveedor",
      width: 220,
      render: (_, record) => (
        <Tooltip title={record.nombre}>
          <div style={{ maxWidth: 220 }}>
            <Text style={clamp(2)}>{record.nombre}</Text>
          </div>
        </Tooltip>
      )
    },
    {
      title: "Cantidad",
      width: 120,
      align: "center",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{formatNumber(record.cantidad_comprada)}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.unidad_medida}</Text>
        </Space>
      )
    },
    {
      title: "Stock",
      width: 110,
      align: "center",
      render: (_, record) => {
        const stock = Number(record.stock_disponible || 0);
        let color = "green";
        if (stock === 0) color = "orange";
        if (stock < 0) color = "red";
        return (
          <Tooltip title={`Entregado: ${formatNumber(record.cantidad_entregada)}`}>
            <Tag color={color} style={{ marginRight: 0 }}>{formatNumber(stock)}</Tag>
          </Tooltip>
        );
      }
    },
    {
      title: "Precio unitario",
      width: 140,
      align: "right",
      render: (_, record) => <Text>$ {formatMoney(record.valor_unitario_COP)}</Text>
    },
    {
      title: "Valor total",
      width: 150,
      align: "right",
      render: (_, record) => <Text strong>$ {formatMoney(record.valor_total_COP)}</Text>
    },
    {
      title: "Entrega PO",
      width: 115,
      render: (_, record) => {
        const fecha = record.fecha_entrega_po || record.fecha_estimada_entrega;
        return <Text>{fecha ? dayjs(fecha).format("DD/MM/YYYY") : "—"}</Text>;
      }
    },
    {
      title: "Acciones",
      width: 90,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Space>
          {permissionUpdate && (
            <Tooltip title="Editar">
              <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(record)} />
            </Tooltip>
          )}
          {permissionDelete && (
            <Popconfirm title="¿Eliminar producto?" description="Queda registrado en el histórico." okText="Eliminar" cancelText="Cancelar" onConfirm={() => onDelete(record)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      {permissionDelete && selectedRowKeys.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Popconfirm title={`¿Eliminar ${selectedRowKeys.length} registros?`} onConfirm={handleDeleteSelected} okText="Eliminar" cancelText="Cancelar">
            <Button danger>Eliminar seleccionados ({selectedRowKeys.length})</Button>
          </Popconfirm>
        </div>
      )}
      <Table
        rowKey="oid"
        columns={columns}
        dataSource={data}
        loading={loading}
        rowSelection={permissionDelete ? rowSelection : null}
        bordered
        size="middle"
        scroll={{ x: 1600 }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "30"],
          showTotal: (total) => `Total ${total} registros`
        }}
        onChange={(pager) => setPagination({ current: pager.current, pageSize: pager.pageSize })}
        style={{ borderRadius: 10, overflow: "hidden" }}
      />
    </div>
  );
}
